/**
 * MODULE: _shared/frontend-shell/src/features/crm/utils/pipelineRunTerritoryStore.ts
 * PURPOSE: Persist pipeline generation runs by territory so AccountDrawer can show counts without lifting full page state into CrmContext
 * DEPENDENCIES: None
 * PUBLIC_API: recordPipelineRunMemory, countPipelineRunsForTerritory, subscribePipelineRunMemory
 * SIDE_EFFECTS: localStorage read or write; window CustomEvent on record
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 75/150
 * RECENT: 2026-04-30 [2.2.742] Territory run memory for AccountDrawer pipeline count; docs sync — CHANGELOG [2.2.742]
 */

const STORAGE_KEY = 'brazn_crm_pipeline_runs_v1';
const EVT = 'brazn-crm-pipeline-run-recorded';

export type PipelineRunMemory = {
  id: string;
  atISO: string;
  territoryId: string;
};

function readAll(): PipelineRunMemory[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (!Array.isArray(arr)) return [];
    return arr.filter(
      (x): x is PipelineRunMemory =>
        x != null &&
        typeof x === 'object' &&
        typeof (x as PipelineRunMemory).territoryId === 'string' &&
        typeof (x as PipelineRunMemory).id === 'string'
    );
  } catch {
    return [];
  }
}

function writeAll(rows: PipelineRunMemory[]) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(rows.slice(0, 100)));
}

/** Called after a successful pipeline generation run (same session as PipelineGenerationPage). */
export function recordPipelineRunMemory(territoryId: string, id?: string): void {
  const row: PipelineRunMemory = {
    id:
      id ??
      (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `run-${Date.now()}`),
    atISO: new Date().toISOString(),
    territoryId,
  };
  writeAll([row, ...readAll()]);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVT, { detail: { territoryId } }));
  }
}

export function countPipelineRunsForTerritory(territoryId: string | null | undefined): number {
  if (!territoryId) return 0;
  return readAll().filter((r) => r.territoryId === territoryId).length;
}

export function subscribePipelineRunMemory(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const h = () => onChange();
  window.addEventListener(EVT, h);
  window.addEventListener('storage', h);
  return () => {
    window.removeEventListener(EVT, h);
    window.removeEventListener('storage', h);
  };
}
